"use client";

import { useState, useEffect, useCallback } from "react";
import {
  getAuthState,
  login as authLogin,
  register as authRegister,
  logout as authLogout,
  forgotPassword as authForgotPassword,
  resetPassword as authResetPassword,
  updateStoredName,
} from "@/app/lib/auth";

interface UseAuthReturn {
  isAuthenticated: boolean;
  email: string | null;
  name: string;
  isAdmin: boolean;
  helpViewCount: number;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (inviteCode: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Sync the locally-cached display name after a successful PUT /account/name. */
  setName: (name: string) => void;
}

export function useAuth(): UseAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [name, setNameState] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [helpViewCount, setHelpViewCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Check session storage on mount
  useEffect(() => {
    const state = getAuthState();
    if (state) {
      setIsAuthenticated(true);
      setEmail(state.email);
      setNameState(state.name ?? "");
      setIsAdmin(state.isAdmin ?? false);
      setHelpViewCount(state.helpViewCount ?? 0);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (emailVal: string, password: string) => {
    const token = await authLogin(emailVal, password);
    setIsAuthenticated(true);
    setEmail(emailVal);
    setNameState(token.name ?? "");
    setIsAdmin(token.is_admin ?? false);
    setHelpViewCount(token.help_view_count ?? 0);
  }, []);

  const register = useCallback(
    async (inviteCode: string, emailVal: string, password: string) => {
      const token = await authRegister(inviteCode, emailVal, password);
      setIsAuthenticated(true);
      setEmail(emailVal);
      setNameState(token.name ?? "");
      setIsAdmin(token.is_admin ?? false);
      setHelpViewCount(token.help_view_count ?? 0);
    },
    []
  );

  const logout = useCallback(() => {
    authLogout();
    setIsAuthenticated(false);
    setEmail(null);
    setNameState("");
    setIsAdmin(false);
    setHelpViewCount(0);
  }, []);

  // Sync the locally-cached display name after a successful
  // PUT /account/name — updates both component state and sessionStorage.
  const setName = useCallback((nextName: string) => {
    updateStoredName(nextName);
    setNameState(nextName);
  }, []);

  const forgotPassword = useCallback(async (emailVal: string) => {
    await authForgotPassword(emailVal);
  }, []);

  const resetPassword = useCallback(
    async (emailVal: string, code: string, newPassword: string) => {
      await authResetPassword(emailVal, code, newPassword);
    },
    []
  );

  return {
    isAuthenticated,
    email,
    name,
    isAdmin,
    helpViewCount,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    setName,
  };
}
